"use client";

import { useState, useEffect } from "react";
import Link from "next/link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Info, Gem } from "lucide-react";

interface Limits {
  remainingMonthly: number;
  remainingStorage: number | "Unlimited";
  planTier: string;
}

interface MonthlyLimitDisplayProps {
  requiredCredits: number;
}

export default function MonthlyLimitDisplay({
  requiredCredits = 0,
}: MonthlyLimitDisplayProps) {
  const [limits, setLimits] = useState<Limits | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLimits = async () => {
      try {
        const response = await fetch("/api/user/limits");
        if (response.ok) {
          const data = await response.json();
          setLimits(data);
        }
      } catch (error) {
        console.error("Error fetching limits:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLimits();
  }, []);

  if (isLoading) return null;

  // Consistent tier mapping as pricing page
  const tierDisplayMap: Record<string, string> = {
    FREE: "Free",
    PRO: "Pro",
    PREMIUM: "Premium",
    PLUS: "Plus",
    free: "Free",
    pro: "Pro",
    premium: "Premium",
    plus: "Plus",
  };
  const isValidCredits = requiredCredits > 0;
  const remainingCredits = limits?.remainingMonthly ?? 0;
  const hasEnoughLimit = remainingCredits >= requiredCredits;
  const displayTier = limits?.planTier ? tierDisplayMap[limits.planTier] || limits.planTier : "Free";
  const isProUser = displayTier === "Pro";

  return (
    <div>
      <Alert
        className={
          isValidCredits
            ? hasEnoughLimit
              ? "bg-indigo-50 border-indigo-200 py-2 px-3 rounded-lg"
              : "bg-red-50 border-red-200 py-2 px-3 rounded-lg"
            : "bg-amber-50 border-amber-200 py-2 px-3 rounded-lg"
        }
      >
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              {isValidCredits ? (
                <Badge 
                  variant={hasEnoughLimit ? "default" : "destructive"}
                  className="text-xs py-1 px-2 font-semibold"
                >
                  {requiredCredits} credits required
                </Badge>
              ) : (
                <div className="flex items-center gap-1.5 text-amber-700 text-xs font-medium">
                  <Info className="h-3.5 w-3.5" />
                  <span>Invalid request</span>
                </div>
              )}
              <Badge
                variant={isProUser ? "secondary" : "default"}
                className={`text-xs py-1 px-2 font-semibold ${
                  isProUser
                    ? "bg-purple-100 text-purple-800 border-purple-200"
                    : ""
                }`}
              >
                {displayTier} PLAN
              </Badge>
              
              <span className="text-xs text-gray-700 font-medium">
                {remainingCredits} credits left
                {!isValidCredits ? null : (
                  !hasEnoughLimit && (
                    <span className="ml-1.5 text-red-600 font-semibold">
                      (need {requiredCredits - remainingCredits} more)
                    </span>
                  )
                )}
              </span>
            </div>
          </div>
          
          {!isProUser && (
            <Link href="/pricing" className="sm:ml-auto">
              <Button
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white h-8 text-xs px-3 font-semibold shadow-sm hover:shadow-md transition-all"
                size="sm"
              >
                <Gem className="w-3.5 h-3.5 mr-1.5" />
                Upgrade
              </Button>
            </Link>
          )}
        </div>
      </Alert>
    </div>
  );
}
